import { z } from 'zod';
/**
 * Payload shape for `GET /api/dashboard/settlement-summary`.
 *
 * "What does EVI owe my building, and when does the next
 * payout arrive?" The first question has a real answer; the
 * second is honest about the missing infrastructure.
 *
 * v1 semantics: lifetime unsettled
 * ───────────────────────────────
 * No SettlementBatch model exists today (filed for future at
 * `docs/tickets/settlement-batching.md`). The widget aggregates
 * `Payout.breakdown.electricity_cost_cents` across in-scope
 * payouts with no period filter. `next_settlement_date` is
 * always null and `settlement_cadence: 'manual'` drives the
 * "Settlement cadence is manual today" empty-state copy.
 *
 * Once the batch model lands, this response shape grows a
 * `current_period` + `next_settlement_date` and the cadence
 * flips to `'monthly'`. The frontend can detect the upgrade
 * via the cadence field.
 *
 * Status filter
 * ─────────────
 * Only `paid` and `transferred` Payout statuses count. Money
 * that hasn't actually arrived from the driver yet (`pending`,
 * `payment_pending`, `payment_failed`) is not "owed to the
 * building" — it's bad debt or in-flight collection. Failed,
 * refunded, and disputed are explicitly excluded.
 */
export const SettlementCadenceSchema = z.enum(['manual', 'monthly']);
export const SettlementCurrencyBucketSchema = z.object({
    currency: z.string().nullable(),
    /** Sum of electricity_cost_cents across qualifying payouts in
     *  scope. The "EVI owes the building" figure. Held payouts are not
     *  in it (see held_electricity_cents). */
    total_owed_cents: z.number().int().nonnegative().nullable(),
    /** How many payouts contributed (held payouts excluded). */
    session_count: z.number().int().nonnegative(),
    /** Collect and hold (2026-09-27): the Site's electricity on HELD
     *  payouts, where settlement refused the split because that cost
     *  exceeded the session's energy revenue. Their breakdown records $0
     *  electricity, so total_owed_cents cannot carry it; this is the
     *  building's pending figure, paid manually once the rate is sorted
     *  (Payout.held_electricity_cost_cents). Same status filter. */
    held_electricity_cents: z.number().int().nonnegative().nullable(),
    /** How many held payouts held_electricity_cents covers. */
    held_session_count: z.number().int().nonnegative(),
    /** Earliest payout in the unsettled window. ISO date.
     *  Null when count = 0. Drives the "since X" subline. */
    oldest_session_date: z.string().nullable(),
    /** Most recent payout in the unsettled window. */
    newest_session_date: z.string().nullable(),
});
export const SettlementSummaryResponseSchema = z.object({
    money_by_currency: z.array(SettlementCurrencyBucketSchema),
    session_count: z.number().int().nonnegative(),
    held_session_count: z.number().int().nonnegative(),
    oldest_session_date: z.string().nullable(),
    newest_session_date: z.string().nullable(),
    /** v1: always null. When formal batching ships
     *  (settlement-batching.md), populated with the next batch's
     *  expected close date. */
    next_settlement_date: z.string().nullable(),
    /** Drives the empty-state copy hierarchy. v1 always emits
     *  'manual'; will flip to 'monthly' when the batch model
     *  lands. */
    settlement_cadence: SettlementCadenceSchema,
    is_scoped: z.boolean(),
    generated_at: z.string(),
});
//# sourceMappingURL=dashboard-settlement-summary.js.map